import { useEffect, useState } from "react";
import { fetchTours } from "../services/api";
import { TOURS as FALLBACK_TOURS } from "../data/tours";

/**
 * Tour catalogue loader.
 *
 * Tours, their line-ups, and their city schedules live in the database and are
 * edited in admin/tours.php. The bundled catalogue in src/data/tours.js is the
 * fallback for when the API is unreachable or the tours migration has not run,
 * so the booking pages always render something.
 *
 * The request is shared across every component that calls the hook — the pages
 * mount together during navigation and only one round trip is needed.
 */
let cachedRequest = null;

function loadTours() {
  if (!cachedRequest) {
    cachedRequest = fetchTours()
      .then((tours) => (tours.length ? tours : FALLBACK_TOURS))
      .catch(() => FALLBACK_TOURS);
  }
  return cachedRequest;
}

/** Drop the cache so the next mount re-reads the API (used after admin edits). */
export function invalidateTours() {
  cachedRequest = null;
}

export function useTours() {
  const [tours, setTours] = useState(null);

  useEffect(() => {
    let active = true;
    loadTours().then((result) => {
      if (active) setTours(result);
    });
    return () => {
      active = false;
    };
  }, []);

  return { tours: tours ?? FALLBACK_TOURS, loading: tours === null };
}

/** "Two tours" rather than "2 tours" — the catalogue size is admin-driven now. */
const NUMBER_WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six"];
export const tourCountWord = (total) => NUMBER_WORDS[total] ?? String(total);

/** Convenience wrapper for the single-tour page. */
export function useTour(slug) {
  const { tours, loading } = useTours();
  return { tour: tours.find((item) => item.slug === slug), tours, loading };
}
